/**
 * Here we export a custom definition of MathJS
 *
 * Based on the example provided by MathJS
 * More info - https://mathjs.org/examples/advanced/custom_evaluate_using_import.js.html
 *
 * This is done so that we can treat null values as the identity value in arithmetic operations
 * More info - https://en.wikipedia.org/wiki/Identity_element
 */

import { create, all, factory, larger, mod } from 'mathjs';

const _someArgumentIsNullOrUndefined = (...args) => args.some((arg) => [null, undefined].includes(arg));

/**
 * We need to overload the behavior of the evaluate method, so it accepts comparisions between arrays.
 * This way, we can handle this kind of expressions:
 * [20, 30] == [20, 40] & 210 != [204, 205, 208, 210] | [60, 70, 80] == 80
 * More info: https://github.com/josdejong/mathjs/blob/develop/examples/advanced/custom_relational_functions.js
 */
const allWithCustomFunctions = {
  ...all,
  createEqual: factory(
    'equal',
    [],
    () =>
      function equal(a, b) {
        /**
         * When any of the operands has an array format, it is called a "dense matrix".
         * Here, we check if both of the operands are a dense matrix ([50, 60] == [60, 70])
         * or if only the first one is so ([50, 60] == 60) or the second one is so (60 == [60, 70])
         */
        if (a?.isDenseMatrix && b?.isDenseMatrix) {
          return a._data.some((aValue) => b._data.some((bValue) => aValue === bValue));
        } else if (a?.isDenseMatrix) {
          return a._data.includes(b);
        } else if (b?.isDenseMatrix) {
          return b._data.includes(a);
        }

        return a == b;
      }
  ),
  createUnequal: factory(
    'unequal',
    [],
    () =>
      function unequal(a, b) {
        /**
         * When any of the operands has an array format, it is called a "dense matrix".
         * Here, we check if both of the operands are a dense matrix ([50, 60] != [60, 70])
         * or if only the first one is so ([50, 60] != 60) or the second one is so (60 != [60, 70])
         */
        if (a?.isDenseMatrix && b?.isDenseMatrix) {
          return !a._data.some((aValue) => b._data.some((bValue) => aValue === bValue));
        } else if (a?.isDenseMatrix) {
          return !a._data.includes(b);
        } else if (b?.isDenseMatrix) {
          return !b._data.includes(a);
        }

        return a != b;
      }
  ),
};

/**
 * Create a mathjs instance with hardly any functions.
 * There are some functions created which are used internally by evaluate though,
 * for example by the Unit class which has dependencies on addScalar, subtract,
 * multiplyScalar, etc.
 */
const pixMath = create(allWithCustomFunctions);

/**
 * Custom implementations of all functions you want to support
 *
 * By defining this functions, null values are converted to 0 (aka. operator identity)
 * if (a === null || a === undefined || b === null || b === undefined) {
 *    Console.warn('Tried to sum null values. Converted null to 0');
 * }
 */
const add = (a, b) => {
  const safeA = Number(a);
  const safeB = Number(b);
  return safeA + safeB;
};
const subtract = (a, b) => {
  const safeA = Number(a);
  const safeB = Number(b);
  return safeA - safeB;
};
const multiply = (a, b) => {
  const safeAndNeutral = (n) => {
    if (n === undefined && n === null) {
      return 1;
    }
    return Number(n);
  };
  const safeA = safeAndNeutral(a);
  const safeB = safeAndNeutral(b);
  return safeA * safeB;
};

const divide = (a, b) => {
  return _someArgumentIsNullOrUndefined(a, b) ? NaN : a / b;
};

const toUpperCase = (a) => {
  if (typeof a === 'string') {
    return a.toUpperCase();
  } else {
    return a;
  }
};

pixMath.import(
  {
    subtract,
    multiply,
    divide,
    mod,
    larger,
    add,
    toUpperCase,
    evaluate: pixMath.evaluate,
  },
  { override: true }
);

export default pixMath;
